/**
 * Video player for How-To and SOP videos (ADR-0012). Same frame and stamp as the
 * PDF viewer. The play link lasts 60 minutes; if it runs out mid-video the
 * player asks for a fresh one and carries on from the same spot.
 */
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Stamp } from "./PdfViewer";

export function VideoViewer({ url, placeholder, stamp, tools, onExpired, allowDownload }: {
  url: string | null;
  placeholder?: ReactNode;
  stamp: Stamp;
  tools?: ReactNode;
  /** Fetch a fresh play link (the old one expired). */
  onExpired: () => Promise<string | null>;
  allowDownload: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const retried = useRef(false);
  const current = src ?? url;

  const onError = async () => {
    const el = ref.current;
    if (!el || retried.current) { setFailed(true); return; }
    retried.current = true;
    const at = el.currentTime, playing = !el.paused;
    const fresh = await onExpired().catch(() => null);
    if (!fresh) { setFailed(true); return; }
    setSrc(fresh);
    requestAnimationFrame(() => {
      const v = ref.current;
      if (!v) return;
      v.currentTime = at;
      if (playing) void v.play().catch(() => undefined);
    });
  };

  return (
    <section className="viewer" aria-label="Video player">
      <div className="vbar">
        <span className="vlabel">▶ Video</span>
        {tools}
      </div>
      <div className="vbody">
        <div className="pdf-wrap video-wrap">
          {placeholder ? <div className="viewer-msg">{placeholder}</div>
            : failed ? <div className="viewer-msg"><div><b>The video couldn't be played.</b>Your browser may not support this format. Ask the author for an .mp4 copy.</div></div>
            : !current ? <div className="viewer-msg"><div className="spin">Loading video…</div></div>
            : (
              <figure className={`video-frame ${stamp.draft ? "draft" : ""}`}>
                <div className="video-stamp top">{stamp.watermark}</div>
                <video ref={ref} src={current} controls preload="metadata" playsInline
                  controlsList={allowDownload ? undefined : "nodownload"}
                  onLoadedData={() => { retried.current = false; }}
                  onError={() => void onError()} />
                <figcaption className="video-stamp foot"><span>{stamp.left}</span><span>{stamp.right}</span></figcaption>
              </figure>
            )}
        </div>
      </div>
    </section>
  );
}
